import React from "react";
import './SmartDataOptions.scss';
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useFlags } from "launchdarkly-react-client-sdk";
import { selectSelectedTab } from "store/workspace/slice";
import { exploreSlice } from "store/explore/slice";
import { getPlotThunk, T_getHistogramPlotData } from "store/explore/thunk";
import { gearIcon } from "@progress/kendo-svg-icons";
import { lightbulbIcon } from "helpers/icons/customIcons";
import { useNavigate } from "react-router-dom";
import { DATA_PAGE_TABS, ROUTES } from "helpers/constants/app";

const CROSSPLOT_TAB_TYPE = 1;
const HISTOGRAM_TAB_TYPE = 2;
const GRID_TAB_TYPE = 4;

// Anywhere else the group would be a button that changes nothing.
const IMPUTABLE_TAB_TYPES = [CROSSPLOT_TAB_TYPE, HISTOGRAM_TAB_TYPE, GRID_TAB_TYPE];

export const SmartDataOptions: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { workspaceId, plotId: workspacePlotId } = useAppSelector(state => state.workspaceSlice);
    const showImputedData = useAppSelector(state => state.exploreSlice.showImputedData);
    const selectedTab = useAppSelector(selectSelectedTab);
    const plotId = selectedTab?.id || workspacePlotId;

    // The LaunchDarkly flag is 'bt-1962-imputation' (PlotController.ImputationFlagKey).
    // The React SDK camel-cases keys, so it arrives here as 'bt1962Imputation'.
    const flags = useFlags();

    const handleToggleImputedData = async () => {
        dispatch(exploreSlice.actions.setShowImputedData(!showImputedData));

        // Imputed values are calculated server side and only returned when asked for, so the plot
        // has to be refetched rather than filtered locally. Both thunks read the toggle back off
        // the store, which the dispatch above has already written.
        if (!plotId || !workspaceId)
            return;

        if (selectedTab?.type === HISTOGRAM_TAB_TYPE) {
            await dispatch(T_getHistogramPlotData({ workspaceId, plotId }));
        } else if (selectedTab?.type === CROSSPLOT_TAB_TYPE) {
            await dispatch(getPlotThunk({ plotId, workspaceId }));
        }

        // A grid fetches a page at a time out of its own local state, so there is no whole-plot
        // thunk to call here. GridView watches the toggle in the store instead.
    };

    if (!flags['bt1962Imputation'] || !IMPUTABLE_TAB_TYPES.includes(selectedTab?.type as number))
        return null;

    return (
        <span className="smartDataActionsGroup">
            <SvgIcon className="smartDataActionsGroupLabel" icon={lightbulbIcon} />
            <span className="smartDataActionButtons">
                {/* The label stays put while the colour carries the state, rather than flipping to
                    "Hide": the filter group's buttons are icons that need a title to say what they
                    do, and this one does not. */}
                <Button
                    className="smartDataToggle"
                    themeColor={showImputedData ? "primary" : "light"}
                    onClick={handleToggleImputedData}
                >
                    Show Imputed Data
                </Button>
                <Button
                    title="Choose which imputation models to use"
                    svgIcon={gearIcon}
                    themeColor="light"
                    onClick={() => navigate(`${ROUTES.data}?tab=${DATA_PAGE_TABS.imputationModels}`)}
                />
            </span>
        </span>
    );
};
